/**
 * Pure validation/normalization helpers shared by services, hooks, and the
 * native reference implementations. No Firebase, no React.
 */

/** Max length of a post caption. Enforced client-side before write. */
export const MAX_MESSAGE_LENGTH = 280

/** Handle rules: 3–20 chars, lowercase letters, numbers, and underscores. */
export const HANDLE_MIN_LENGTH = 3
export const HANDLE_MAX_LENGTH = 20
const HANDLE_PATTERN = /^[a-z0-9_]{3,20}$/

/**
 * Normalize raw handle input toward storage form: strip a leading `@`, trim,
 * lowercase. Storage is always without `@`; the UI re-adds it for display.
 */
export const normalizeHandle = (raw: string): string =>
  raw.trim().replace(/^@+/, '').toLowerCase()

/** True if a normalized handle satisfies the handle rules. */
export const isValidHandle = (raw: string): boolean =>
  HANDLE_PATTERN.test(normalizeHandle(raw))

/** Prefix a stored handle with `@` for display. */
export const displayHandle = (handle: string): string => `@${handle}`

/** True if a caption is within the allowed length. */
export const isValidMessage = (message: string): boolean =>
  message.length <= MAX_MESSAGE_LENGTH

/**
 * Normalize a social handle for storage: strip a leading `@`/whitespace and any
 * accidentally pasted profile URL down to the username. Empty input -> null.
 */
export const normalizeSocial = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  const trimmed = raw.trim().replace(/^@+/, '')
  if (!trimmed) return null
  // If a full URL was pasted, keep only the last path segment.
  const fromUrl = trimmed.match(/[/]([^/?#]+)[/]?(?:[?#].*)?$/)
  const value = fromUrl ? fromUrl[1] : trimmed
  return value || null
}
